<template>
  <Layout>
    <template #bars-mobile>
      <BarsMobile
        :income="income"
        :inflation="altersvorsorge.inflation"
        :monetary-need="monetaryNeed"
        :labels="labels"
      >
        <template #incomeBar>
          <money-input v-model="income" />
        </template>
        <template #segmentsBar>
          <Segment
            :height="altersvorsorge.gesetzlicheRente / monetaryNeed"
            horizontal
            class="text-sm"
          >
            Gesetzl. Rente<br>
            <div class="flex">
              <div class="grow flex">
                <money-input v-model="altersvorsorge.gesetzlicheRente" />
              </div>
            </div>
          </Segment>
          <Segment
            v-if="altersvorsorge.bestehendeVorsorge > 0"
            :height="altersvorsorge.bestehendeVorsorge / monetaryNeed"
            light
            horizontal
            class="text-sm"
          >
            Private Vorsorge
            <money-input v-model="altersvorsorge.bestehendeVorsorge" />
          </Segment>
          <Segment
            v-if="altersvorsorge.versorgungswerk > 0"
            :height="altersvorsorge.versorgungswerk / monetaryNeed"
            :light="altersvorsorge.bestehendeVorsorge <= 0"
            horizontal
            class="text-sm"
          >
            Versorgungswerk
            <money-input v-model="altersvorsorge.versorgungswerk" />
          </Segment>
          <Segment
            v-if="gap > 0"
            :height="gap / monetaryNeed"
            dashed-outline
            horizontal
            class="text-secondary text-sm"
          >
            Lücke
            <strong v-text="format(gap)" />
          </Segment>
        </template>
      </BarsMobile>
    </template>
    <template #bars-desktop>
      <BarsDesktop
        :income="income"
        :inflation="altersvorsorge.inflation"
        :monetary-need="monetaryNeed"
        :labels="labels"
        :gap="gap"
      >
        <template #incomeBar>
          <MoneyInput
            v-model="income"
            placeholder="z.B. 2.000 €"
            class="text-gray-800"
          />
        </template>
        <template #segmentsBar>
          <Segment
            v-if="gap > 0"
            :height="gap / monetaryNeed"
            dashed-outline
          >
            Versorgungslücke<br>
            {{ format(gap) }}
          </Segment>
          <Segment
            v-if="altersvorsorge.versorgungswerk > 0"
            :height="altersvorsorge.versorgungswerk / monetaryNeed"
            :light="altersvorsorge.bestehendeVorsorge <= 0"
          >
            Versorgungswerk
            <MoneyInput
              v-model="altersvorsorge.versorgungswerk"
              placeholder="z.B. 2.000 €"
              class="text-gray-800"
            />
          </Segment>
          <Segment
            v-if="altersvorsorge.bestehendeVorsorge > 0"
            :height="altersvorsorge.bestehendeVorsorge / monetaryNeed"
            light
          >
            Private Vorsorge
            <MoneyInput
              v-model="altersvorsorge.bestehendeVorsorge"
              placeholder="z.B. 2.000 €"
              class="text-gray-800"
            />
          </Segment>
          <Segment :height="altersvorsorge.gesetzlicheRente / monetaryNeed">
            Gesetzl. Rente<br>
            <div class="flex">
              <div class="grow flex">
                <MoneyInput
                  v-model="altersvorsorge.gesetzlicheRente"
                  placeholder="z.B. 2.000 €"
                  class="text-gray-800"
                />
              </div>
            </div>
          </Segment>
        </template>
      </BarsDesktop>
    </template>

    <template #controls>
      <div class="col-12">
        <ControlRow
          class="mb-7"
          title="Rentenbeginn"
        >
          <Slider
            v-model="altersvorsorge.rentenbeginn"
            :min="commonProps.age"
            :max="70"
            :format="{suffix: 'J.'}"
            :min-label="`${commonProps.age}J.`"
            max-label="70J."
          />
        </ControlRow>
      </div>
      <div class="col-12">
        <ControlRow
          class="mb-7"
          title="Rentenbedarf"
        >
          <Slider
            v-model="altersvorsorge.rentenbedarf"
            :max="100"
            :format="{suffix: '%'}"
            min-label="0%"
            max-label="100%"
          />
        </ControlRow>
      </div>
      <div class="col-12">
        <ControlRow
          class="mb-7"
          title="Inflation*"
        >
          <Slider
            v-model="altersvorsorge.inflation"
            :max="5"
            :step="0.1"
            :format="{suffix: '%', decimals: 1}"
            min-label="0%"
            max-label="5%"
          />
        </ControlRow>
      </div>

      <div class="col-12">
        <ControlRow>
          <template #title>
            Private Vorsorge<br><small>(Bereits vorhanden)</small>
          </template>
          <MoneyInput
            v-model="altersvorsorge.bestehendeVorsorge"
            placeholder="z.B. 2.000 €"
            class="mb-1.5"
          />
          <div class="text-center text-sm">
            Monatlicher Rentenanspruch
          </div>
        </ControlRow>
      </div>
      <div class="col-12">
        <ControlRow title="Versorgungswerk">
          <MoneyInput
            v-model="altersvorsorge.versorgungswerk"
            placeholder="z.B. 2.000 €"
            class="mb-1.5"
          />
        </ControlRow>
      </div>
    </template>
    <template #rates>
      <div class="col-12 col-lg-6 offset-lg-6">
        <p class="text-sm mt-5 text-muted text-right mb-0">
          * Durchschnittliche Inflation der letzten 60 Jahre: <b>2,7%</b>
        </p>
      </div>
      <Rates
        v-model:ansparzinssatz="altersvorsorge.ansparzinssatz"
        v-model:entnahmezinssatz="altersvorsorge.entnahmezinssatz"
        :age="commonProps.age"
        :eintrittsalter="altersvorsorge.rentenbeginn"
        :gap="gap"
        gap-name="Rentenlücke"
      />
    </template>
  </Layout>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';

import MoneyInput from '@/application/components/form-elements/MoneyInput/MoneyInput.vue';
import Slider from '@/application/components/Slider.vue';
import BarsDesktop from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/components/BarsDesktop.vue';
import BarsMobile from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/components/BarsMobile.vue';
import ControlRow from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/components/ControlRow.vue';
import Layout from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/components/Layout.vue';
import Rates from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/components/Rates.vue';
import Segment from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/components/Segment.vue';
import { calculateMonetaryNeed } from '@/bedarf/components/altersvorsorge/altersvorsorge/chart/utils';
import { useAltersvorsorgeStore } from '@/bedarf/components/altersvorsorge/altersvorsorge/store';
import { format } from '@/application/utils/money';

import { chartProps } from './chartProps';

export default defineComponent({
  components: {
    BarsDesktop,
    BarsMobile,
    Layout,
    Segment,
    ControlRow,
    Slider,
    MoneyInput,
    Rates,
  },

  props: chartProps,

  setup(props) {
    const labels = {
      income: 'Nettoeinkommen',
      popoverText: 'Schreiben Sie Ihr Nettoeinkommen in das vorgegebene Feld',
      monetaryNeed: 'Rentenbeginn',
    };
    const income = ref(props.commonProps.income || 2000);
    const currentYear = new Date().getFullYear();

    const { altersvorsorge, patchDefaults } = useAltersvorsorgeStore();

    patchDefaults(props.data);

    const monetaryNeed = computed(() => {
      const duration = altersvorsorge.rentenbeginn - props.commonProps.age;
      return calculateMonetaryNeed(income.value, altersvorsorge.rentenbedarf, duration, altersvorsorge.inflation);
    });

    const gap = computed(() => Math.max(
      0,
      monetaryNeed.value
        - altersvorsorge.bestehendeVorsorge
        - altersvorsorge.versorgungswerk
        - altersvorsorge.gesetzlicheRente,
    ));

    return {
      // static
      labels,
      currentYear,

      // reactive
      altersvorsorge,
      income,

      // computed
      monetaryNeed,
      gap,

      // methods
      format,
    };
  },
});
</script>
